import axios, { AxiosRequestConfig, Method, ResponseType } from 'axios';
import { getAccessToken } from 'utils/auth';
import { AUTH, REST_URL } from 'constants/index';
import AppService from './app.service';
import { getMarketplaceToken, isRequireMarketplaceToken } from 'utils/authMarketplace';
import queryString from 'query-string';
import store from 'redux/store';
import { checkValidURL } from 'utils/function';

interface QueryParams {
  [key: string] : any;
}

export const handleBaseURL = (baseApim: string) => {
  const bootstrapConfig = JSON.parse(sessionStorage.getItem(AUTH.CONFIGURATION) || '');
  const isLocalhost = bootstrapConfig?.environment === 'local' ||
    window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    );
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseApi = isLocalhost ? bootstrapConfig?.baseAPI : apimService?.apimUrl;
  const baseAPIM = `${baseApim}${bootstrapConfig?.baseUID ? `-${bootstrapConfig?.baseUID}` : ''}`;
  const baseURL = `${checkValidURL(baseAPIM) ? '' : baseApi ? baseApi : bootstrapConfig?.baseAPI}${baseAPIM}`;
  return baseURL;
}

export interface RequestParams {
  url: string;
  method: string;
  queryParams?: QueryParams;
  data?: any;
  options?: {
    'Content-Type'?: string;
    noCache?: boolean;
    'Ocp-Apim-Subscription-Key'?: string;
  };
  responseType?: ResponseType;
  baseURL?: string;
}

const instance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params) => queryString.stringify(params),
})

instance.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  const reduxState = store.getState();

  const isAuthenticatedByAzure = AppService.isAuthenticatedByAzure;
  const authenticateBy = (isAuthenticatedByAzure() ? 'AAD' : sessionStorage.getItem(AUTH.AUTH_BY)) || 'OKTA';
  const apimService = reduxState?.configReducer?.generalConfig?.uptimeApimServices;
  const isMarketplaceEnabled = !!reduxState.configReducer.permission?.marketplace?.enabled;
 
  if (config.headers) {
    config.headers['Authorization'] = token;
    config.headers['Authentication-Server'] = authenticateBy;
    if (isMarketplaceEnabled && isRequireMarketplaceToken(config, apimService)) {
      const marketplaceToken = await getMarketplaceToken(config);
      config.headers['marketplace_token'] = marketplaceToken;
    }
  }

  return config;
});

instance.interceptors.response.use(
  (response) => checkStatus(response),
  (error) => {
    console.error('Error', JSON.stringify(error));
    throw error;
  },
);

export default async function request(requestParams: RequestParams) {
  const {
    url,
    method,
    queryParams,
    data,
    options,
    responseType,
    baseURL,
  } = requestParams;

  let headers = {};

  if (options) {
    if (options['Content-Type'] !== 'notset')
      headers = { ...headers, ...options };
    if (options['noCache']) {
      const noCache = {
        pragma: 'no-cache',
        'cache-control': 'no-cache',
      };
      headers = { ...headers, ...noCache };
    }
  }
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const config: AxiosRequestConfig = {
    method: method as Method,
    headers,
    params: queryParams,
    data,
    responseType,
    baseURL: baseURL ?? `${handleBaseURL(apimService?.baseApiSuffix)}`,
  };

  const result = await instance(url, config);
  return result as any;
}

export async function requestWithRetry(
  requestParams: RequestParams,
  retryTime: number = 3,
  sleeptime: number = 200,
) {
  let retries = 0;

  while (retries < retryTime) {
    try {
      const response = await request(requestParams);
      return response;
    } catch (error) {
      if (retries === retryTime - 1) {
        throw error;
      }

      retries++;
      await new Promise(resolve => setTimeout(resolve, sleeptime));
    }
  }
}

function checkStatus(response: any = {}) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  const error = new Error(response.statusText);
  error.stack = response;
  throw error;
}

export async function requestAudit(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.auditApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestCatalog(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.catalogApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestMarketplace(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.marketplaceApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestEmployeeBoarding(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.employeeOnboardingApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestProvisioning(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.provisioningApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestKnowledge(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.knowledgeApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestPCL(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.pclApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestOrder(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.orderApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestNotification(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.notificationApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestUser(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.userApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestConfig(requestParams: RequestParams) {
  const bootstrapConfig = JSON.parse(sessionStorage.getItem(AUTH.CONFIGURATION) || '');
  const baseURL = `${bootstrapConfig?.configAPI ? bootstrapConfig?.configAPI : (window.location.origin + REST_URL.CONFIG)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestAsset(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.assetApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestIncident(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.incidentApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestShipping(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.shippingApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestDeviceManagement(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.deviceManagementApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestM365(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.m365ApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}

export async function requestCatalogSR(requestParams: RequestParams) {
  const apimService = store.getState()?.configReducer?.generalConfig?.uptimeApimServices;
  const baseURL = `${handleBaseURL(apimService?.catalogSRApiSuffix)}`;
  return request({ ...requestParams, baseURL });
}